import React, { useState, useEffect } from 'react'

import {
  Modal,
  Stack,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material'
import { LockOutlined, Visibility, VisibilityOff, SaveOutlined } from '@mui/icons-material'

import { HttpError } from '@/api/http'

import { ConfirmSaveModalProps } from './types'

const ConfirmSaveModal: React.FC<ConfirmSaveModalProps> = ({ open, onClose, onConfirm, lockoutStatus, refetchLockoutStatus }) => {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isLocked = lockoutStatus?.is_locked ?? false

  useEffect(() => {
    if (isLocked) {
      onClose()
    }
  }, [isLocked, onClose])

  const handleConfirm = async () => {
    if (!password) {
      setError('Informe a senha atual.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await onConfirm(password)
      setPassword('')
    } catch (e) {
      const httpError = e as HttpError
      const message = httpError.message || 'Ocorreu um erro ao salvar as alterações.'
      if (httpError.status === 400 && httpError.code === 'wrong_password') {
        setError(null)
      } else {
        setError(message)
      }
    } finally {
      setIsLoading(false)
      refetchLockoutStatus?.()
    }
  }

  const handleClose = () => {
    setPassword('')
    setError(null)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleConfirm()
    }
  }

  return (
    <Modal open={open} onClose={handleClose} aria-describedby="confirm-save-modal-description">
      <Stack
        direction="column"
        alignItems="center"
        width={340}
        bgcolor="background.paper"
        borderRadius={4}
        padding={3}
        spacing={2}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          outline: 'none',
        }}
      >
        <Stack fontSize={64}>
          <LockOutlined color="primary" fontSize="inherit" />
        </Stack>

        <Stack spacing={0.5} alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            Confirmar alterações
          </Typography>
          <Typography
            id="confirm-save-modal-description"
            variant="body2"
            textAlign="center"
            color="text.secondary"
          >
            Para salvar as alterações, informe sua senha atual.
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ width: '100%', borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {lockoutStatus && !isLocked && lockoutStatus.attempts > 0 && (
          <Alert severity="warning" sx={{ width: '100%', borderRadius: 2 }}>
            <Typography variant="body2">
              Você errou a senha {lockoutStatus.attempts} de 3 vezes. Tente novamente.
            </Typography>
          </Alert>
        )}


        <TextField
          fullWidth
          label="Senha atual"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading || isLocked}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />

        <Stack direction="column" width="100%" spacing={1}>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isLoading || isLocked}
            startIcon={<SaveOutlined />}
            sx={{ borderRadius: 20 }}
            disableElevation
          >
            {isLoading ? 'Salvando...' : 'Confirmar'}
          </Button>
          <Button onClick={handleClose} disabled={isLoading} sx={{ borderRadius: 20 }}>
            Cancelar
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}

export default ConfirmSaveModal
